import { A11yModule } from '@angular/cdk/a11y';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoDirective } from '@jsverse/transloco';
import { PlatformService } from '@ld.consumer-portal/utils';

export type ModalSize = 'large' | 'medium' | 'fit';

@Component({
  selector: 'ld-base-modal',
  imports: [A11yModule, MatIconModule, NgTemplateOutlet, TranslocoDirective],
  templateUrl: './base-modal.component.html',
  styleUrl: './base-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseModalComponent implements OnInit {
  private readonly platformService = inject(PlatformService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly shouldAutoCaptureFocus = computed(() => !this.platformService.isNativeMobile());

  title = input.required<string>();

  headerIcon = input<string>();

  headerIconClass = input<string>();

  headerIconColor = input<string>();

  headerImage = input<string>();

  customMaxWidth = input<string>();

  size = input<ModalSize>('medium');

  showCloseButton = input<boolean>(true);

  closeOnBackdropClick = input<boolean>(true);

  isLoading = input<boolean>(false);

  ariaLabelledBy = input<string>('base-modal-title');

  critical = input<boolean>(false);

  embedded = input<boolean>(false);

  closeModal = output<void>();

  private static openModalCount = 0;

  ngOnInit(): void {
    if (!this.embedded()) {
      BaseModalComponent.openModalCount++;
      document.body.style.overflow = 'hidden';
      this.destroyRef.onDestroy(() => {
        BaseModalComponent.openModalCount--;
        if (BaseModalComponent.openModalCount <= 0) {
          BaseModalComponent.openModalCount = 0;
          document.body.style.overflow = '';
        }
      });
    }
  }

  protected onClose(): void {
    if (!this.isLoading()) {
      this.closeModal.emit();
    }
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && this.closeOnBackdropClick() && !this.isLoading()) {
      this.closeModal.emit();
    }
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !this.isLoading()) {
      this.closeModal.emit();
    }
  }
}
